import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { store } from "Store";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { Row, Col, Card, Button } from "react-bootstrap";
import { ICONS } from "Utils/icons";
import { TextButton } from "Components/Button";
import { validateToken } from "./FirstTimeAxiosFuncs";
import { PATHS } from "Utils/PagePaths";
import { maskEMail } from "Utils/utilityFunctions";

const FirstTimeSelectMFA = () => {

  const navigate = useNavigate();
  const { token } = useParams();
  const loginState = store.getState().loginInfo;
  const dispatch = useDispatch();
  const location = useLocation();
  const isForward = location.state?.isForward;

  const [isLoading, setIsLoading] = useState(true);
  // "SMS" or "EMAIL"
  const [activeMethod, setActiveMethod] = useState("");
  const [mfaFactors, setMfaFactors] = useState({
    isEmailMfaEnabled: null,
    isSmsMfaEnabled: null,
    mfaEmail: null
  });

  const handleSelectMethod = (method) => {
    setActiveMethod(method);
  }

  const handleNext = (method) => {

    // navigate to SMS MFA
    if (method === "SMS") {
      navigate("/activation/token/" + token + "/4_1", { state: { isForward: true } });
    }

    // navigate to Email MFA
    if (method === "EMAIL") {
      navigate("/activation/token/" + token + "/4_2", { state: { isForward: true } });
    }
  }

  const cancelMfa = () => {
    navigate(PATHS.public['Sign In']);
  }

  // init load to validate the token in the URL from the email
  const initLoad = async (token) => {
    // the Reset Password MFA can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to reset password.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });

      return;
    }

    const res = await validateToken(token)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });

    //console.log("initLoad() first time password", res);
    if (res && res.data?.status !== 1) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: res.data.globalErrorMessageList,
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Sign In'],
          data: {}
        }
      });
      return;
    }

    if (res && res.data?.status === 1) {

      // from the step of the first time reset pwd
      if (isForward) {
        let isSmsEnabled = res.data.data?.supportedMfaTypes?.includes("SMS");
        let isEmailEnabled = res.data.data?.supportedMfaTypes?.includes("EMAIL");

        // both SMS and Email are enabled for setup, stay on the page
        if (isSmsEnabled && isEmailEnabled) {
          setIsLoading(false);
          setMfaFactors({
            isSmsMfaEnabled: isSmsEnabled,
            isEmailMfaEnabled: isEmailEnabled,
            mfaEmail: res.data.data?.email
          });
          return;
        }

        // only SMS are enabled for setup
        if (isSmsEnabled) {
          navigate("/activation/token/" + token + "/4_1", { state: { isForward: true } });
          return;
        }

        // only Email are enabled for setup
        if (isEmailEnabled) {
          navigate("/activation/token/" + token + "/4_2", { state: { isForward: true } });
          return;
        }

        return;
      }

      // go back to the term of use page to start over the validation flow
      navigate("/activation/token/" + token);
      return;
    }
  }

  useEffect(() => {
    initLoad(token);
  }, [])

  return (
    <>
      {isLoading ? null :
        <Row className="mb-3">
          <Col xl={6} lg={6} md={11} sm={12}>
            <Card id="firsttimepwd-select-mfa" className="p-4">
              <Card.Body className="text-start">
                <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
                  Two-Factor Authentication
                </h1>
                <hr></hr>
                <p>
                  Please select a setup method.
                </p>
                {mfaFactors.isSmsMfaEnabled ?
                  <Row>
                    {/* <Col></Col> */}
                    <Col md={12} sm={12} xl={8}>
                      <Button
                        className={`w-100 ${activeMethod === "SMS" ? 'mfa-method-button-active' : ''}`}
                        onClick={() => handleSelectMethod("SMS")}
                      >
                        <div class="mfa-method">
                          <span class="mfa-icon">{ICONS.sms()}</span>
                          <span class="text-start">
                            <span className="mfa-method-title">SMS</span>
                            <br />
                            <span className="mfa-method-desc">Register the mobile device</span>
                          </span>
                        </div>
                      </Button>
                      <hr></hr>
                    </Col>
                    <Col></Col>
                  </Row>
                  : null}
                {mfaFactors.isEmailMfaEnabled ?
                  <Row>
                    {/* <Col></Col> */}
                    <Col md={12} sm={12} xl={8}>
                      <Button
                        className={`w-100 ${activeMethod === "EMAIL" ? 'mfa-method-button-active' : ''}`}
                        onClick={() => handleSelectMethod("EMAIL")}
                      >
                        <div class="mfa-method">
                          <span class="mfa-icon">{ICONS.mailoutlined()}</span>
                          <span class="text-start">
                            <span className="mfa-method-title">Email</span>
                            <br />
                            <span className="mfa-method-desc">Send a code via email to {maskEMail(mfaFactors.mfaEmail)}</span>
                          </span>
                        </div>
                      </Button>
                    </Col>
                    <Col></Col>
                  </Row>
                  : null}
                <hr></hr>
                <Row className="mb-3 pt-1">
                  <Col>
                    <TextButton
                      className="w-10 me-3"
                      disabled={activeMethod === ""}
                      variant="normal"
                      onClick={() => handleNext(activeMethod)}
                      content={"Next"}
                    >
                    </TextButton>
                    <TextButton
                      className="w-10"
                      variant="outline"
                      onClick={cancelMfa}
                      content={"Cancel"}
                    >
                    </TextButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row >}
    </>
  )
}

export default FirstTimeSelectMFA;